import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import type { PropertyTagsUser } from 'lumen/models';
import type CustomTagValue from 'lumen/models/custom-tag-value';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';
import { timeout } from 'lumen/utils/waiters';

interface UserOption {
  id: string;
  label: string;
  user: PropertyTagsUser;
}

interface Signature {
  Args: {
    cellData: {
      isMixed?: boolean;
      onUpdate: (newValues: PropertyTagsUser[]) => void;
      subscriptionIds: string[];
      values: CustomTagValue[];
    };
    row: {
      isDisabled?: boolean;
      isReadOnly: boolean;
    };
  };
}

export default class SettingsCustomPropertyTagsCellsTdMultiselectUser extends Component<Signature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];

  @tracked isOpen = false;
  @tracked options: UserOption[] = [];
  @tracked optionsFilter = '';
  @tracked page = 0;
  @tracked stagedValue: UserOption[] | null = null;
  @tracked totalPages = 1;

  #userOptionCache = new Map<string, UserOption>();

  fetchOptionsTask = task(
    { restartable: true },
    async (): Promise<UserOption[]> => {
      const requestParams: Record<string, unknown> = {
        namespace: APIGATEWAY,
        typeKey: 'property-tags/autocomplete/users',
        page: this.page,
        market_id: this.hotelAndMarketService.market?.id,
        subscription_id: this.args.cellData.subscriptionIds,
      };

      if (this.optionsFilter) {
        requestParams['q'] = this.optionsFilter;
      }

      try {
        const users = await this.store.query<PropertyTagsUser>(
          'property-tags/user',
          // @ts-expect-error: Incorrect type
          requestParams,
        );

        const meta = users.meta as unknown as {
          results_count: number;
          results_per_page: number;
        };

        this.totalPages = Math.ceil(meta.results_count / meta.results_per_page);

        return users.map((user) => this.userOptionFor(user));
      } catch (error) {
        this.errorTracking.captureException(error);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  loadMoreTask = task({ drop: true }, async (): Promise<void> => {
    if (this.page + 1 < this.totalPages) {
      this.page += 1;
      const fetched = await this.fetchOptionsTask.perform();
      this.options = [...this.options, ...fetched];
    }
  });

  searchOptionTask = task(
    { restartable: true },
    async (filter: string): Promise<void> => {
      await timeout();
      this.optionsFilter = filter;
      this.page = 0;
      this.options = await this.fetchOptionsTask.perform();
    },
  );

  get currentValue(): UserOption[] {
    return this.stagedValue ?? this.selectedOptions;
  }

  get readOnlyLabel(): string {
    const labels = this.selectedOptions.map((o) => o.label);
    return labels.length > 0
      ? labels.join(', ')
      : this.intl.t('general.missing');
  }

  get selectedOptions(): UserOption[] {
    return this.args.cellData.values
      .map((v) => v.user)
      .filter((u): u is PropertyTagsUser => u !== null && u !== undefined)
      .map((u) => this.userOptionFor(u));
  }

  userOptionFor(user: PropertyTagsUser): UserOption {
    const id = user.id ?? '';
    const cached = this.#userOptionCache.get(id);

    if (cached && cached.user === user) {
      return cached;
    }

    const option = { id, label: user.name, user };
    this.#userOptionCache.set(id, option);

    return option;
  }

  @action buildLabelWithCount(count: number): string {
    if (count === 0 && this.args.cellData.isMixed) {
      return this.intl.t('general.mixed');
    }
    if (count === 1) {
      return this.selectedOptions[0]?.label ?? '';
    }
    return this.intl.t('general.values_selected', { count });
  }

  @action handleChange(selected: UserOption[] | null): void {
    this.stagedValue = null;
    this.args.cellData.onUpdate((selected ?? []).map((opt) => opt.user));
  }

  @action toggleDropdown(): void {
    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.stagedValue = [...this.selectedOptions];
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.searchOptionTask.perform(this.optionsFilter);
    } else {
      this.stagedValue = null;
      this.optionsFilter = '';
      this.page = 0;
      this.options = [];
    }
  }

  <template>
    {{#if @row.isReadOnly}}
      <span>{{this.readOnlyLabel}}</span>
    {{else}}
      <div class="flex flex-col">
        <PrismComboSelectInput
          @buildLabelWithCount={{this.buildLabelWithCount}}
          @canUnselectAll={{true}}
          @disabled={{@row.isDisabled}}
          @isSearchable={{true}}
          @isSmall={{true}}
          @onChange={{this.handleChange}}
          @onClose={{this.toggleDropdown}}
          @onOpen={{this.toggleDropdown}}
          @onSearch={{this.searchOptionTask.perform}}
          @options={{this.options}}
          @placeholder={{if
            @cellData.isMixed
            (t "general.mixed")
            (t "settings.property_tags.autocomplete_user.placeholder")
          }}
          @unsafe_onLoadMore={{this.loadMoreTask.perform}}
          @value={{this.currentValue}}
          data-test-id="td-multiselect-user"
        />
      </div>
    {{/if}}
  </template>
}
